import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { TrashEntry, UserWorkspace } from '@/api/types'
import { settingsQuery } from '@/features/settings/queries'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { NativeSelect } from '@/shared/ui/native-select'
import { Skeleton } from '@/shared/ui/skeleton'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import { whenText } from '@/shared/lib/when'
import { describeTrashEntry, isRestorable } from '../describe'
import {
  useEmptyTrash,
  useRestoreFromTrash,
  useTrashContents,
  useTrashStructure,
} from '../queries'

/**
 * What has been deleted from this workspace, and the way to put it back.
 *
 * Scoped by workspace and optionally by application, because that is how the
 * endpoint pages: the two scopes are different sequences of responses rather
 * than one list filtered two ways.
 *
 * `initialApplicationId` picks the scope the dialog opens on and nothing more:
 * the select is still there and still reaches the whole workspace. Opened from
 * an application's own menu it starts on that application, which is what was
 * asked for; opened from the workspace it starts on everything.
 */
export function TrashDialog({
  workspace,
  initialApplicationId = null,
  open,
  onOpenChange,
}: {
  workspace: UserWorkspace
  initialApplicationId?: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const [applicationId, setApplicationId] = useState<number | null>(initialApplicationId)
  const { data: settings } = useQuery(settingsQuery())
  const structure = useTrashStructure()

  const here = structure.data?.workspaces.find((entry) => entry.id === workspace.id)
  const hours = settings?.frontend.hours_until_trash_permanently_deleted

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('trash.title')}</DialogTitle>
          {hours !== undefined && hours !== null && (
            <DialogDescription>
              {t('trash.retention', { count: hours })}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground" htmlFor="trash-scope">
            {t('trash.scope')}
          </label>
          <NativeSelect
            id="trash-scope"
            value={applicationId === null ? '' : String(applicationId)}
            onChange={(event) =>
              setApplicationId(
                event.target.value === '' ? null : Number(event.target.value),
              )
            }
          >
            <option value="">
              {t('trash.wholeWorkspace', { name: workspace.name })}
            </option>
            {(here?.applications ?? []).map((application) => (
              <option key={application.id} value={application.id}>
                {application.name}
              </option>
            ))}
          </NativeSelect>
        </div>

        {/* Keyed by scope: a different scope is a different paged sequence, and
            paging on into it from page three of the previous one would skip. */}
        <Contents
          key={`${workspace.id}:${String(applicationId)}`}
          workspaceId={workspace.id}
          applicationId={applicationId}
          workspaceTrashed={here?.trashed === true}
          applicationTrashed={
            (here?.applications ?? []).find((a) => a.id === applicationId)?.trashed ===
            true
          }
        />
      </DialogContent>
    </Dialog>
  )
}

function Contents({
  workspaceId,
  applicationId,
  workspaceTrashed,
  applicationTrashed,
}: {
  workspaceId: number
  applicationId: number | null
  workspaceTrashed: boolean
  applicationTrashed: boolean
}) {
  const { t } = useTranslation()
  const { data, isPending, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useTrashContents(workspaceId, applicationId)
  const empty = useEmptyTrash()
  const [confirming, setConfirming] = useState(false)

  if (isPending) return <Skeleton className="h-40 w-full" />

  const entries = data?.pages.flatMap((page) => page.results) ?? []

  return (
    <>
      <ul className="max-h-80 divide-y overflow-y-auto rounded-md border">
        {entries.length === 0 ? (
          <li className="p-6 text-center text-sm text-muted-foreground">
            {t('trash.empty')}
          </li>
        ) : (
          entries.map((entry) => (
            <TrashRow
              key={entry.id}
              entry={entry}
              restorable={isRestorable(entry, {
                workspace: workspaceTrashed,
                application: applicationTrashed,
              })}
            />
          ))
        )}
      </ul>

      <div className="flex items-center justify-between gap-2">
        {hasNextPage ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {t('trash.more')}
          </Button>
        ) : (
          <span />
        )}
        <Button
          variant="outline"
          size="sm"
          disabled={entries.length === 0 || empty.isPending}
          onClick={() => setConfirming(true)}
        >
          {t('trash.emptyAction')}
        </Button>
      </div>

      {confirming && (
        <ConfirmDialog
          open
          onOpenChange={() => setConfirming(false)}
          title={t('trash.emptyTitle')}
          description={t('trash.emptyDescription')}
          confirmLabel={t('trash.emptyAction')}
          destructive
          onConfirm={() => {
            empty.mutate({ workspaceId, applicationId })
            setConfirming(false)
          }}
        />
      )}
    </>
  )
}

function TrashRow({ entry, restorable }: { entry: TrashEntry; restorable: boolean }) {
  const { t } = useTranslation()
  const restore = useRestoreFromTrash()
  const description = describeTrashEntry(entry)

  return (
    <li className="flex items-center justify-between gap-3 p-3">
      <div className="min-w-0">
        <p className="truncate text-sm">{t(description.key, description.values)}</p>
        <p className="truncate text-xs text-muted-foreground">
          {t('trash.trashedBy', {
            user: entry.user_who_trashed,
            when: whenText(entry.trashed_at),
          })}
        </p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        disabled={!restorable || restore.isPending}
        title={restorable ? undefined : t('trash.cannotRestore')}
        onClick={() => restore.mutate(entry)}
      >
        {t('trash.restore')}
      </Button>
    </li>
  )
}
